import Utils from "../../../utils";
import RenderManager from "../../../rendering/render-manager";
import TextLiveLabel from "../../../2DObjects/TextLiveLabel";
import SlideManager from "../../../utils/slide-manager";
import createDefaultTextLivelabel from "./default-text-livelabel";

const REGEXP_TRANSLATION = /^.+(_(?<lang>\w{2,4}))$/;
class MeshLabel {
    /**
     *
     * @param {THREE.Object3D} object
     * @param {String} uuid
     */
    constructor(object, uuid) {
        this._object = object;
        this._labels = [new LiveLabel(this, 0, uuid)];
        this._activeRegion = 0;
    }

    _getActiveLabel() { return this._labels[this._activeRegion]; }

    _addRegion(uuid) {
        const label = new LiveLabel(this, this._labels.length);

        this._labels.push(label);
        label._isValid = true;

        return label;
    }
}

class LiveLabel {
    /**
     *
     * @param {MeshLabel} meshLabel
     * @param {number} index
     * @param {string} uuid
     */
    constructor(meshLabel, index, uuid) {
        this._meshLabel = meshLabel;
        this._index = index;
        this._instantiated = false;
        this._isValid = false;
        this._isTurnOn = true;
        this._position = new THREE.Vector3();
        this.type = "LiveLabel";
        this.isLivelabel = true;

        this._expandedLabelData = {
            content: ""
        };

        this._element = new TextLiveLabel(this, createDefaultTextLivelabel(), uuid);

        this._testObject = new THREE.Mesh(new THREE.BoxBufferGeometry(0.5, 0.5, 0.5));
        this._testObject.material.depthTest = false;
        this._testObject.material.depthWrite = false;
        this._testObject.material.transparent = true;

        if (Utils.queryParams.has("debug_depth")) {
            RenderManager.activeScene.add(this._testObject);
        }

        if (Utils.queryParams.has("debug_depth_scale"))
            this._testObject.scale.set(1, 1, 1).multiplyScalar(Utils.queryParams.getNumber("debug_depth_scale"));

        this._material = new THREE.MeshBasicMaterial({
            color: Math.round(0xffffff * Math.random()),
            opacity: 1,
            // transparent: true,
            // depthTest: false,
            // depthWrite: false
            // side: THREE.DoubleSide
        });

        if (!Utils.queryParams.has("disable_labels"))
            RenderManager.overlay2d.appendChild(this._element.domElement);

        this._highlighted = false;
        this._isDemandLabel = false;

        this._state = new LabelState();
    }

    get uuid() { return this._element.uuid; }
    get name() { return this._element.content; }
    get index() { return this._index; }
    get object() { return this._meshLabel._object; }
    get element() { return this._element; }
    get isInitialized() { return this._instantiated; }
    get extendedData() { return this._expandedLabelData; }
    get isHighlighted() { return this._highlighted; }
    get isVisible() { return this._state.visible; }
    get isObstructedBy2d() { return this._state.obstructedBy2d; }
    get isDemandLabel() { return this._isDemandLabel; }
    set isDemandLabel(val) { this._isDemandLabel = val; }
    get isTurnOn() { return this._isTurnOn; }
    set isTurnOn(val) { this._isTurnOn = val; }

    _setHighlighted(isHighlighted) {
        this._highlighted = isHighlighted;
        this._element._highlighted = isHighlighted;

        const textElement = this._element.textElement;

        const baseColor = isHighlighted
            ? '\#' + new THREE.Color(Q3.data.highlightColor || 0x77ff77).getHexString()
            : this._element.color;
        const complimentaryColor = Utils.hexToComplimentary(baseColor);

        textElement.style.color = baseColor;
        this._element.setOutlineColor(complimentaryColor);
    }

    deserialize(object, data) {
        const el = this._element;

        el.color = data.color || el.color;
        el.border = data.border || el.border;
        el.textAlign = data.textAlign || el.textAlign;
        el.fontSize = data.fontSize || el.fontSize;
        el.fontSizeMin = data.fontSizeMin || el.fontSizeMin;
        el.fontWeight = data.fontWeight || el.fontWeight;
        el.distanceMin = data.distanceMin || el.distanceMin;
        el.distanceMax = data.distanceMax || el.distanceMax;
        el.textScale = data.textScale || el.textScale;
        el.content = data.content || el.content;
        el.definition = data.definition || el.definition;
        el.hoverText = data.hoverText || el.hoverText;
        el.globalVisibility = data.globalVisibility || el.globalVisibility;
        el.visibilityType = data.visibilityType || el.visibilityType;
        el.backgroundOpacity = data.backgroundOpacity || el.backgroundOpacity;
        el.paddingHorizontal = data.paddingHorizontal || el.paddingHorizontal;
        el.paddingVertical = data.paddingVertical || el.paddingVertical;
        el.backgroundColor = data.backgroundColor || el.backgroundColor;
        el.backgroundRadius = data.backgroundRadius || el.backgroundRadius;
        el.uuid = data.uuid || el.uuid;

        // console.log(REGEXP_TRANSLATION)
        Object.keys(data).forEach(key => {

            if (!key.match(REGEXP_TRANSLATION)) return;

            el[key] = data[key];
        });

        this._isValid = true;
        this._instantiated = true;
        this._expandedLabelData = data.expanded || {};
        this._isTurnOn = data.isTurnOn;

        const matrix = new THREE.Matrix4();
        if (data.blobs && data.blobs instanceof Array) {
            const len = data.blobs.length;
            if (len % 16 !== 0) throw new Error("Blobs should multiples of 16.");

            // const raycaster = RenderManager.raycaster;

            for (let i = 0; i < len; i += 16) {
                matrix.fromArray(data.blobs, i);

                const blob = makeBlob(object, this);
                matrix.decompose(blob.position, blob.quaternion, blob.scale);
            }
        }

        if (this._element) {
            this._element.createHoverText();
        }
    }

    serialize(object) {
        const el = this._element;
        const subMeshIndex = this._index - 1;

        function getBlobs() {
            /**
             * @type {THREE.Group}
             */
            const subs = object.subRegions.children[subMeshIndex];
            if (!subs || subs.children.length === 0) return null;

            const out = [];

            subs.children.forEach(blob => {
                blob.updateMatrix();
                out.push.apply(out, blob.matrix.toArray());
            });


            return out;
        }

        const serialized = {
            color: el.color,
            border: el.border,
            textAlign: el.textAlign,
            fontSize: el.fontSize,
            fontSizeMin: el.fontSizeMin,
            fontWeight: el.fontWeight,
            distanceMin: el.distanceMin,
            distanceMax: el.distanceMax,
            textScale: el.textScale,
            definition: el.definition,
            hoverText: el.hoverText,
            globalVisibility: el.globalVisibility,
            visibilityType: el.visibilityType,
            isTurnOn: this.isTurnOn,
            uuid: el.uuid,
            expanded: this._expandedLabelData || {},
            blobs: getBlobs(),
            index: this._index,
            content: el.content,
            backgroundColor: el.backgroundColor,
            backgroundOpacity: el.backgroundOpacity,
            paddingHorizontal: el.paddingHorizontal,
            paddingVertical: el.paddingVertical,
            backgroundRadius: el.backgroundRadius
        };

        Object.keys(el).forEach(key => {
            if (!key.match(REGEXP_TRANSLATION)) return;

            serialized[key] = el[key];
        });

        return serialized;
    }

    /**
     * @param {THREE.Mesh} object
     * @param {LabelDTO} dto
     */
    fromDTO(object, dto) {
        const el = this._element;

        el.content = dto.textContent;

        Object.keys(el).forEach(key => {
            const match = key.match(REGEXP_TRANSLATION);

            if (!match) return;

            const lang = match.lang;

            el[`textContent_${lang}`] = dto[`textContent_${lang}`];
        });

        el.fontSize = dto.textSize;
        el.fontSizeMin = dto.textSizeMin;
        el.fontWeight = dto.textWeight;
        el.color = dto.textColor;
        el.backgroundOpacity = dto.backgroundOpacity;
        el.paddingHorizontal = dto.paddingHorizontal;
        el.paddingVertical = dto.paddingVertical;
        el.backgroundRadius = dto.backgroundRadius;
        el.backgroundColor = dto.backgroundColor;
        el.border = dto.textBorder;
        el.textAlign = dto.textAlign;
        el.distanceMin = dto.distanceMin;
        el.distanceMax = dto.distanceMax;
        el.textScale = dto.textScale;
        el.definition = dto.definition;
        el.hoverText = dto.hoverText;
        el.globalVisibility = dto.globalVisibility;
        el.visibilityType = dto.visibilityType;

        if (dto.instantiate && !this._instantiated) {
            this._instantiated = true;
        }
    }

    getDTO(params = {}) { return new LabelDTO(this, params.regionCount, params.activeRegion); }
}

class LabelDTO {
    /**
     *
     * @param {LiveLabel} label
     * @param {Number} regions
     * @param {Number} activeRegion
     */
    constructor(label, regions, activeRegion) {
        const el = label._element;

        this.textContent = el.content;

        Object.keys(el).forEach(key => {
            const match = key.match(REGEXP_TRANSLATION);

            if (!match) return;

            const lang = match.groups.lang;

            this[`textContent_${lang}`] = el[`content_${lang}`];
        });

        this.textSize = el.fontSize;
        this.textWeight = el.fontWeight;
        this.textBorder = el.border;
        this.textAlign = el.textAlign;
        this.distanceMin = el.distanceMin;
        this.distanceMax = el.distanceMax;
        this.textSizeMin = el.fontSizeMin;
        this.definition = el.definition;
        this.hoverText = el.hoverText;
        this.objectUuid = label.object.uuid;
        this.uuid = label.uuid;
        this.textScale = el.textScale;
        this.globalVisibility = el.globalVisibility;
        this.visibilityType = el.visibilityType;

        this.region = activeRegion;
        this.regions = regions;

        let slideData = null;

        if (SlideManager.currentSlide.labels) {
            if (SlideManager.currentSlide.labels.live) {
                slideData = SlideManager.currentSlide.labels.live[label.uuid];
            }
        }

        this.textColor = !slideData || slideData["textColor"] === undefined ? el.color : slideData["textColor"];
        this.backgroundOpacity = !slideData || slideData["backgroundOpacity"] === undefined ? el.backgroundOpacity : slideData["backgroundOpacity"];
        this.paddingHorizontal = !slideData || slideData["paddingHorizontal"] === undefined ? el.paddingHorizontal : slideData["paddingHorizontal"];
        this.paddingVertical = !slideData || slideData["paddingVertical"] === undefined ? el.paddingVertical : slideData["paddingVertical"];
        this.backgroundRadius = !slideData || slideData["backgroundRadius"] === undefined ? el.backgroundRadius : slideData["backgroundRadius"];
        this.backgroundColor = !slideData || slideData["backgroundColor"] === undefined ? el.backgroundColor : slideData["backgroundColor"];

        Object.freeze(this);
    }

    getTextContent(language) {
        return language === "eng" || language === undefined
            ? this.textContent
            : `textContent_${language}` in this
                ? this[`textContent_${language}`]
                : this.textContent;
    }
}

class LabelState {
    constructor() {
        this.visible = false;
        this.obstructedBy2d = false;
        this.positionOriginal = new THREE.Vector3();
        this.positionDesired = new THREE.Vector3();
        this.rayValid = new THREE.Vector2();
        this.rayCurrent = new THREE.Vector2();
        this.lerp = 1;
        this.lerping = false;
        this.depth = 0;
        this.forcedVisible = false;

        Object.seal(this);
    }
}

class PotentialLabel {
    constructor({ label, bbox, regionId, point }) {
        /**
         * @type {LiveLabel}
         */
        this.label = label;
        this.bbox = bbox;
        this.z = label._element.globalVisibility
            ? -1e10 - point.z
            : label.isHighlighted
                ? -Infinity
                : point.z;
        this.regionId = regionId;
        this.point = new THREE.Vector3().copy(point);

        this.calloutData = {
            lx: NaN,
            ly: NaN,
            index: -1
        };

        Object.freeze(this);
    }
}

function makeBlob(object, label) {
    const index = label.index;
    const geometry = new THREE.CircleBufferGeometry(1, 20);
    const mesh = new THREE.Mesh(geometry, label._material);
    mesh.scale.set(0.5, 0.5, 0.5);
    mesh.frustumCulled = false;

    object.subRegions.children[index - 1].add(mesh);

    RenderManager.raycaster.makeTrackableBlob(object, mesh, index);

    return mesh;
}

const LabelVisibility_T = Object.freeze({
    ALWAYS: "always",
    HIDDEN: "hidden",
    HOVER: "hover",
    ON_DEMAND: "on_demand"
});

const calcBlobPositionAndQuaternion = (function () {
    const matrixInverse = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const vecLookAt = new THREE.Vector3();
    const finalMatrix = new THREE.Matrix4();

    const inNormal = new THREE.Vector3();

    function calculateSurfaceNormal(polygon) {
        const normal = inNormal.set(0, 0, 0);

        for (let i = 0, len = polygon.length; i < len; i++) {
            const current = polygon[i];
            const next = polygon[(i + 1) % len];

            normal.x += ((current.y - next.y) * (current.z + next.z));
            normal.y += ((current.z - next.z) * (current.x + next.x));
            normal.z += ((current.x - next.x) * (current.y + next.y));

        }

        return normal.negate().normalize();
    }

    return function ({ point: inPoint, face: inFace, scale: inScale, object: inObject }) {
        matrixInverse.getInverse(inObject.matrixWorld);

        quaternion.setFromRotationMatrix(inObject.matrixWorld);

        const attrPosition = inObject.geometry.getAttribute("position");

        const normal = calculateSurfaceNormal(
            ["a", "b", "c"].map(key => {
                return new THREE.Vector3()
                    .fromBufferAttribute(attrPosition, inFace[key])
                    .applyMatrix4(inObject.matrixWorld);
            })
        );

        const point = new THREE.Vector3()
            .copy(normal)
            .negate()
            .multiplyScalar(Math.min(RenderManager.activeCamera.getWorldPosition(new THREE.Vector3()).distanceTo(inPoint) * 0.2, 9e-4))
            .add(inPoint);

        vecLookAt
            .addVectors(point, normal)
            .applyMatrix4(matrixInverse);

        point.applyMatrix4(matrixInverse);

        finalMatrix.lookAt(point, vecLookAt, new THREE.Vector3(0, 1, 0));

        const q = new THREE.Quaternion().setFromRotationMatrix(finalMatrix);

        const s = new THREE.Vector3()
            .setFromMatrixScale(matrixInverse)
            .multiplyScalar(inScale);

        return { position: point, quaternion: q, scale: s, normal };
    }
})();

export { MeshLabel, LiveLabel, makeBlob, PotentialLabel, LabelVisibility_T, calcBlobPositionAndQuaternion };